import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Plus, Trash2, Save, Calendar, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

const CustomSelect = ({ value, onChange, options, placeholder, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex].value);
        }
        break;
    }
  };

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="relative" ref={selectRef}>
      <div
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={`w-full p-2 border border-[#003366]/20 rounded-lg bg-white cursor-pointer flex items-center justify-between transition-all duration-200 hover:border-[#003366]/40 focus:outline-none ${
          isOpen ? 'border-[#003366] ring-2 ring-[#003366]/20' : ''
        }`}
      >
        <span className={selectedOption ? 'text-[#003366]' : 'text-gray-400'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown 
          size={16} 
          className={`text-[#003366]/60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#003366]/20 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {options.map((option, index) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors duration-150 ${
                highlightedIndex === index || value === option.value
                  ? 'bg-[#003366]/10 text-[#003366]' 
                  : 'text-[#003366]/80 hover:bg-[#003366]/5 hover:text-[#003366]'
              }`}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CustomDatePicker = ({ value, onChange, placeholder = "Select date" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleDateSelect = (date) => {
    onChange(formatDate(date));
    setIsOpen(false);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date && 
           date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    if (!value || !date) return false;
    const selectedDate = new Date(value);
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="relative" ref={datePickerRef}>
      <div
        className="w-full p-2 border border-[#003366]/20 rounded-lg focus-within:ring-2 focus-within:ring-[#003366]/20 focus-within:border-[#003366] transition-colors bg-white cursor-pointer flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={value ? "text-[#003366]" : "text-gray-400"}>
          {value ? formatDisplayDate(value) : placeholder}
        </span>
        <Calendar className="w-4 h-4 text-[#003366]" />
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#003366]/20 rounded-lg shadow-lg z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-[#003366]/10">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-[#003366]/5 rounded-full transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-[#003366]" />
            </button>
            
            <span className="font-medium text-[#003366]">
              {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>
            
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-[#003366]/5 rounded-full transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-[#003366]" />
            </button>
          </div>

          {/* Days of week */}
          <div className="grid grid-cols-7 gap-1 p-2 border-b border-[#003366]/10">
            {daysOfWeek.map(day => (
              <div key={day} className="text-center text-xs font-medium text-[#003366]/60 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1 p-2">
            {days.map((date, index) => (
              <button
                key={index}
                type="button"
                onClick={() => date && handleDateSelect(date)}
                disabled={!date}
                className={`
                  h-8 w-8 text-sm rounded-md transition-colors flex items-center justify-center
                  ${!date ? 'invisible' : ''}
                  ${isSelected(date) 
                    ? 'bg-[#003366] text-white' 
                    : isToday(date)
                    ? 'bg-[#003366]/10 text-[#003366] font-medium'
                    : 'text-[#003366] hover:bg-[#003366]/5'
                  }
                `}
              >
                {date?.getDate()}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center p-3 border-t border-[#003366]/10">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
              className="text-sm text-[#003366]/60 hover:text-[#003366] transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                handleDateSelect(new Date());
              }}
              className="text-sm text-[#003366] hover:bg-[#003366]/5 px-2 py-1 rounded transition-colors"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ReleaseConfig = () => {
  const [formData, setFormData] = useState({
    version: "",
    date: "",
    ReleaseType: "Major Release",
    features: [{ title: "", description: "" }],
  });
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const accessToken = useSelector((state) => state.accessToken.value);

  // Release type options for the custom select
  const releaseTypeOptions = [
    { value: "Major Release", label: "Major Release" },
    { value: "Feature Release", label: "Feature Release" },
    { value: "Bug Fix", label: "Bug Fix" },
    { value: "Security Update", label: "Security Update" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    const query = `
      mutation AddReleaseConfiguration($input: ReleaseConfigurationInput!) {
        addReleaseConfiguration(input: $input) {
          id
          version
          date
          ReleaseType
          features {
            title
            description
          }
        }
      }
    `;

    try {
      const response = await axios.post(
        apiUrl,
        {
          query,
          variables: { input: formData },
        },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      if (
        response.data.errors &&
        response.data.errors[0].errorType == "Unauthorized"
      )
        setSubmitStatus({
          type: "error",
          message:
            "Edit access is not granted to your account for this resource.!",
        });
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
      } else {
        setSubmitStatus({
          type: "success",
          message: "Release note added successfully!",
        });
        setFormData({
          version: "",
          date: "",
          ReleaseType: "Major Release",
          features: [{ title: "", description: "" }],
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: `Error: ${error.message}`,
      });
      console.error("Error adding release note:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFeatureChange = (index, field, value) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = {
      ...updatedFeatures[index],
      [field]: value,
    };
    setFormData({ ...formData, features: updatedFeatures });
  };

  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, { title: "", description: "" }],
    });
  };

  const removeFeature = (index) => {
    const updatedFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: updatedFeatures });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#003366]/5 to-white py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {submitStatus.message && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              submitStatus.type === "success"
                ? "bg-green-50 border border-green-200 text-green-800"
                : "bg-red-50 border border-red-200 text-red-800"
            }`}
          >
            {submitStatus.message}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg p-6 border border-[#003366]/10">
          <div className="flex items-center space-x-2 mb-8">
            <div className="w-1 h-8 bg-[#003366] rounded-full"></div>
            <h1 className="text-2xl font-bold text-[#003366]">
              Add New Release
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#003366] mb-2">
                  Version Number
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g., v2.4.0"
                  className="w-full p-2 border border-[#003366]/20 rounded-lg focus:ring-2 focus:ring-[#003366]/20 focus:border-[#003366] outline-none transition-colors"
                  value={formData.version}
                  onChange={(e) =>
                    setFormData({ ...formData, version: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#003366] mb-2">
                  Release Date
                </label>
                <CustomDatePicker
                  value={formData.date}
                  onChange={(date) => setFormData({ ...formData, date })}
                  placeholder="Select release date"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#003366] mb-2">
                Release Type
              </label>
              <CustomSelect
                id="releaseType"
                value={formData.ReleaseType}
                onChange={(e) => setFormData({ ...formData, ReleaseType: e.target.value })}
                options={releaseTypeOptions}
                placeholder="Select release type"
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-[#003366]/10 pb-4">
                <h2 className="text-lg font-semibold text-[#003366]">
                  Features
                </h2>
                <button
                  type="button"
                  onClick={addFeature}
                  className="flex items-center gap-2 text-[#003366] hover:bg-[#003366]/5 px-3 py-1 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Feature
                </button>
              </div>

              {formData.features.map((feature, index) => (
                <div
                  key={index}
                  className="p-4 border border-[#003366]/20 rounded-lg space-y-4 hover:border-[#003366]/40 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <h3 className="text-sm font-medium text-[#003366]">
                      Feature {index + 1}
                    </h3>
                    {formData.features.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <input
                      type="text"
                      required
                      placeholder="Feature Title"
                      className="w-full p-2 border border-[#003366]/20 rounded-lg focus:ring-2 focus:ring-[#003366]/20 focus:border-[#003366] outline-none transition-colors"
                      value={feature.title}
                      onChange={(e) =>
                        handleFeatureChange(index, "title", e.target.value)
                      }
                    />
                    <textarea
                      required
                      placeholder="Feature Description"
                      rows="3"
                      className="w-full p-2 border border-[#003366]/20 rounded-lg focus:ring-2 focus:ring-[#003366]/20 focus:border-[#003366] outline-none transition-colors resize-none"
                      value={feature.description}
                      onChange={(e) =>
                        handleFeatureChange(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-4 border-t border-[#003366]/10">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488] transition-colors ${
                  isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? "Saving..." : "Save Release"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReleaseConfig;